/**
 * Permission checking utilities for sub-page access control
 * CRITICAL: These functions enforce security and data safety
 */

// Truthy check that tolerates legacy/string values ("true", 1, true) saved
// before backend normalization existed. All callers in this module use this.
const _isTruthy = (v) =>
  v === true || v === 'true' || v === 1 || v === '1';

/**
 * Check if user has permission for a specific module action
 * @param {Object} user - The user object with permissions
 * @param {string} module - The module key (e.g., 'maintenance', 'motorcycles')
 * @param {string} action - The action to check ('view', 'create', 'edit', 'delete')
 * @returns {boolean}
 */
export const hasModulePermission = (user, module, action = 'view') => {
  // Super Admin and Admin have full access
  if (user?.role === 'Super Admin' || user?.role === 'Admin') {
    return true;
  }
  
  // Check custom permissions - must be explicitly granted
  if (user?.permissions && user.permissions[module]) {
    return _isTruthy(user.permissions[module][action]);
  }
  
  // SECURITY: Default to FALSE - deny access if not explicitly granted
  return false;
};

/**
 * Check if user has permission for a specific sub-page action
 * @param {Object} user - The user object with permissions
 * @param {string} module - The module key (e.g., 'maintenance')
 * @param {string} subPage - The sub-page key (e.g., 'repairItems', 'technicians')
 * @param {string} action - The action to check ('view', 'create', 'edit', 'delete')
 * @returns {boolean}
 */
export const hasSubPagePermission = (user, module, subPage, action = 'view') => {
  // Super Admin and Admin have full access
  if (user?.role === 'Super Admin' || user?.role === 'Admin') {
    return true;
  }
  
  // First check if user has access to the main module
  if (!hasModulePermission(user, module, 'view')) {
    return false;
  }
  
  // Check sub-page permissions - must be explicitly granted
  const subPagePerms = user?.permissions?.[module]?.subPages?.[subPage];
  if (subPagePerms) {
    return _isTruthy(subPagePerms[action]);
  }
  
  // SECURITY: Default to FALSE - deny access if not explicitly granted
  return false;
};

// Mirrors backend `user_can`: admins always pass, others need the exact tick.
export const userCan = (user, module, action = 'view', subPage = null) => {
  if (user?.role === 'Super Admin' || user?.role === 'Admin') return true;
  const mod = user?.permissions?.[module];
  if (!mod) return false;
  return subPage ? _isTruthy(mod.subPages?.[subPage]?.[action]) : _isTruthy(mod[action]);
};


/**
 * Get all permissions for a module (main + sub-pages)
 * @param {Object} user - The user object with permissions
 * @param {string} module - The module key
 * @returns {Object} - Object with main permissions and subPages permissions
 */
export const getModulePermissions = (user, module) => {
  // Super Admin and Admin have full access
  if (user?.role === 'Super Admin' || user?.role === 'Admin') {
    return {
      view: true,
      create: true,
      edit: true,
      delete: true,
      subPages: 'all' // Special flag indicating full sub-page access
    };
  }
  
  if (user?.permissions && user.permissions[module]) {
    return user.permissions[module];
  }
  
  // SECURITY: Return all false if no permissions
  return {
    view: false,
    create: false,
    edit: false,
    delete: false,
    subPages: {}
  };
};

/**
 * Hook-style function to check multiple permissions at once
 * Returns an object with boolean flags for each permission type
 * @param {Object} user - The user object with permissions
 * @param {string} module - The module key
 * @returns {Object} - Object with permission check functions
 */
export const usePermissions = (user, module) => {
  const isAdmin = user?.role === 'Super Admin' || user?.role === 'Admin';

  // Truthy check that tolerates legacy/string values ("true", 1, true).
  // Some older saves may have persisted these as strings; treat them
  // consistently as booleans here so `viewOwnOnly` filters apply correctly.
  const isTruthy = (v) =>
    v === true || v === 'true' || v === 1 || v === '1';
  const modulePerms = user?.permissions?.[module] || {};
  const rawViewOwnOnly = isTruthy(modulePerms.viewOwnOnly);

  // Diagnostic on hiring module — surfaces production debugging.
  if (typeof window !== 'undefined' && module === 'hiring' && user?.email) {
    // eslint-disable-next-line no-console
    console.log('[usePermissions(utils):hiring]', {
      email: user?.email,
      role: user?.role,
      isAdmin,
      rawViewOwnOnly,
      viewOwnOnlyRawValue: modulePerms.viewOwnOnly,
      effectiveViewOwnOnly: !isAdmin && rawViewOwnOnly,
    });
    if (typeof window !== 'undefined') {
      window.__LAST_HIRING_USER_UTILS = user;
    }
  }

  return {
    isAdmin,
    // Main module permissions
    canView: isAdmin || hasModulePermission(user, module, 'view'),
    canCreate: isAdmin || hasModulePermission(user, module, 'create'),
    canEdit: isAdmin || hasModulePermission(user, module, 'edit'),
    canDelete: isAdmin || hasModulePermission(user, module, 'delete'),
    // Admins always see all — viewOwnOnly only applies to non-admin roles
    viewOwnOnly: !isAdmin && rawViewOwnOnly,
    // Sub-page permission checkers
    canViewSubPage: (subPage) => isAdmin || hasSubPagePermission(user, module, subPage, 'view'),
    canCreateSubPage: (subPage) => isAdmin || hasSubPagePermission(user, module, subPage, 'create'),
    canEditSubPage: (subPage) => isAdmin || hasSubPagePermission(user, module, subPage, 'edit'),
    canDeleteSubPage: (subPage) => isAdmin || hasSubPagePermission(user, module, subPage, 'delete'),
    // Document-specific permission checkers (upload/download/replace)
    canUploadSubPage: (subPage) => isAdmin || hasSubPagePermission(user, module, subPage, 'upload'),
    canDownloadSubPage: (subPage) => isAdmin || hasSubPagePermission(user, module, subPage, 'download'),
    canReplaceSubPage: (subPage) => isAdmin || hasSubPagePermission(user, module, subPage, 'replace'),
  };
};

/**
 * Check if user is Admin or Super Admin
 * @param {Object} user - The user object
 * @returns {boolean}
 */
export const isAdminUser = (user) => {
  return user?.role === 'Super Admin' || user?.role === 'Admin';
};
